import React, {useState, useEffect, useMemo} from 'react';
import Cookies from 'js-cookie';
import LineRevenueChart from "../../../components/charts/LineRevenueChart";
import LineQpsChart from "../../../components/charts/LineQpsChart";
import BarRevenueChart from "../../../components/charts/BarRevenueChart";
import BundleLists from "../../../components/charts/BundleLists";
import MainMap from "../../../components/charts/map/MainMap";
import {Grid, Typography} from "@mui/material";
import {fetchFullRevenueTotals, fetchQpsDaily, fetchQpsWasteful, fetchBundlesBy} from '../../../API/ChartsDashboard';
import {sendDataToApi} from '../../../API/apiHelper';


const REVENUE_SERIES_DEF = [
    {key: 'DSP_SH', label: 'DSP Revenue SH'},
    {key: 'SSP_SH', label: 'SSP Cost SH'},
    {key: 'DSP_RX', label: 'DSP Revenue RX'},
    {key: 'SSP_RX', label: 'SSP Cost RX'},
    {key: 'DSP_SUM', label: 'DSP Sum Revenue'},
    {key: 'SSP_SUM', label: 'SSP Sum Cost'},
];

const totalsByKeyFromFields = (fields) => {
    if (!Array.isArray(fields)) {
        return {};
    }
    return Object.fromEntries(fields.map(({key, value}) => [key, value]));
};

const buildRevenueChartSeries = (byKey) => {
    const base = byKey.DSP_SH;
    if (!base?.length) {
        return {dateLabels: [], series: []};
    }
    const dateLabels = base.map((row) => row.date);
    const series = REVENUE_SERIES_DEF.map(({key, label}) => ({
        key,
        label,
        data: (byKey[key] || []).map((item) => parseFloat(item.total_revenue)),
    }));
    return {dateLabels, series};
};

const HomeDashboard = () => {

    const [revenueByKey, setRevenueByKey] = useState({});
    const [xLabels, setXLabels] = useState([]);

    const [qpsFull, setQpsFull] = useState([]);
    const [qpsWasteful, setQpsWasteful] = useState([]);
    const [bundlesByParamsData, setBundlesByParamsData] = useState([]);

    const userAdmin = Cookies.get('user_admin');
    const boosOfPubManager = Cookies.get('boss_pub_managers');
    const boosOfAcManager = Cookies.get('boss_ac_managers');

    useEffect(() => {

        const fetchQps = async () => {
            try {
                const fields = await fetchQpsDaily();

                setQpsFull(fields);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchBundlesByParams = async () => {
            try {
                const fields = await fetchBundlesBy();

                setBundlesByParamsData(fields);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchQpsWastefulDaily = async () => {
            try {
                const fields = await fetchQpsWasteful();

                setQpsWasteful(fields);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchFullTotals = async () => {
            try {
                const fields = await fetchFullRevenueTotals();
                const byKey = totalsByKeyFromFields(fields);
                setRevenueByKey(byKey);
                const dspSh = byKey.DSP_SH || [];
                setXLabels(dspSh.map((item) => item.date));
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchManagerTotals = async () => {
            const formData = new FormData();
            formData.append('user_id', localStorage.getItem('user_id'));

            const response = await sendDataToApi('post', '/api/charts/revenue/manager-totals', formData);

            if (!response?.data) {
                return;
            }

            const byKey = totalsByKeyFromFields(
                Object.entries(response.data).map(([key, value]) => ({key, value}))
            );
            setRevenueByKey(byKey);
            const dspSh = byKey.DSP_SH || [];
            setXLabels(dspSh.map((item) => item.date));
        };

        fetchBundlesByParams();

        fetchQps();
        fetchQpsWastefulDaily();

        if (userAdmin === '1' || boosOfAcManager === '1' || boosOfPubManager === '1') {
            fetchFullTotals();
        } else {
            fetchManagerTotals();
        }
    }, []);

    const {dateLabels: revenueDateLabels, series: revenueSeries} = useMemo(
        () => buildRevenueChartSeries(revenueByKey),
        [revenueByKey]
    );

    return (
        <div>
            <Grid container spacing={2}
                  sx={{
                      padding: 2,
                      borderRadius: '8px',
                      border: '4px',
                      paddingLeft: 0,
                      marginLeft: '-10px',
                  }}
            >

                <Grid item xs={13}
                      sx={{
                          marginBottom: '26px',
                          ml: 0,
                      }}
                >
                    <MainMap/>
                </Grid>

                <Grid item xs={6}
                      sx={{
                          padding: 2,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '4px',
                          boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                      }}
                >
                    <LineRevenueChart
                        series={revenueSeries}
                        dateLabels={revenueDateLabels.length ? revenueDateLabels : xLabels}
                        nameTitle={'Revenue changes'}
                    />
                </Grid>

                <Grid item xs={6}
                      sx={{
                          padding: 2,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                          marginRight: '-15px',
                          ml: 'auto',
                          width: '70%'
                      }}
                >
                    <BarRevenueChart
                        data={qpsWasteful}
                        nameTitle={'Top 10 Wasteful QPS'}
                    />
                </Grid>

                <Grid item xs={13}
                      sx={{
                          marginTop: '26px',
                          ml: 0,
                      }}
                >
                    <BundleLists
                        budlesData={bundlesByParamsData}
                    />
                </Grid>

                <Grid item xs={13}
                      sx={{
                          padding: 2,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '4px',
                          boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                          marginTop: '26px',
                          marginRight: '-15px',
                          marginLeft: '-20px',
                          ml: 0,
                      }}
                >
                    <LineQpsChart
                        dateLabels={xLabels}
                        nameTitle="QPS Metrics (Last 24 hours)"
                        fullData={qpsFull}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default HomeDashboard;


